import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { Button } from './ui/Button';
import { trpc } from '../lib/trpc';

interface AvatarEditorProps {
  userId: string;
  currentAvatarUrl?: string;
  onAvatarChange?: (url: string | null) => void;
  onCancel?: () => void;
}

export interface AvatarEditorHandle {
  /**
   * If the user has selected and cropped an image but not yet clicked Confirm,
   * commit the pending upload. Resolves once the avatar is persisted (or with
   * no-op if nothing is pending). Rejects on upload failure.
   */
  commitPendingCrop: () => Promise<void>;
  /**
   * Returns true when the editor has a cropped image awaiting confirmation.
   */
  hasPendingCrop: () => boolean;
}

/**
 * Creates a canvas element from the cropped area and returns a blob
 */
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

/**
 * Extracts the cropped area from the image and returns a blob
 */
const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area
): Promise<Blob> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Set canvas size to the cropped area
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Convert canvas to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
};

export const AvatarEditor = forwardRef<AvatarEditorHandle, AvatarEditorProps>(({
  userId,
  currentAvatarUrl,
  onAvatarChange,
  onCancel,
}, ref) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getSignature = trpc.media.getUploadSignature.useMutation();
  const updateAvatar = trpc.user.updateAvatar.useMutation();
  const utils = trpc.useUtils();

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please upload a JPG, PNG, or WebP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result as string);
      setError(null);
    });
    reader.readAsDataURL(file);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const uploadAndSaveAvatar = async (): Promise<void> => {
    if (!imageSrc || !croppedAreaPixels) return;

    setIsUploading(true);
    setError(null);

    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

      const signature = await getSignature.mutateAsync({
        entityType: 'user',
        entityId: userId,
      });

      const formData = new FormData();
      formData.append('file', croppedBlob, 'avatar.jpg');
      formData.append('timestamp', signature.timestamp.toString());
      formData.append('signature', signature.signature);
      formData.append('api_key', signature.apiKey);
      formData.append('folder', signature.folder);
      if (signature.context) {
        formData.append('context', signature.context);
      }

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${signature.cloudName}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to Cloudinary');
      }

      const uploadData = await uploadResponse.json();

      await updateAvatar.mutateAsync({
        avatar: uploadData.secure_url,
      });

      await utils.user.getMe.invalidate();

      if (onAvatarChange) {
        onAvatarChange(uploadData.secure_url);
      }

      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      await uploadAndSaveAvatar();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
    }
  };

  useImperativeHandle(ref, () => ({
    commitPendingCrop: async () => {
      if (!imageSrc || !croppedAreaPixels) return;
      await uploadAndSaveAvatar();
    },
    hasPendingCrop: () => Boolean(imageSrc && croppedAreaPixels),
  }));

  const handleRemove = async () => {
    setIsUploading(true);
    setError(null);

    try {
      await updateAvatar.mutateAsync({
        avatar: null,
      });

      // Invalidate user query so header and profile refresh
      await utils.user.getMe.invalidate();

      if (onAvatarChange) {
        onAvatarChange(null);
      }

      setImageSrc(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove avatar';
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError(null);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="space-y-4">
      {!imageSrc ? (
        <div>
          {/* File picker and drag-and-drop area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileInputChange}
              className="hidden"
            />
            
            {currentAvatarUrl ? (
              <div className="space-y-4">
                <img
                  src={currentAvatarUrl}
                  alt="Current avatar"
                  className="w-32 h-32 rounded-full mx-auto object-cover"
                  referrerPolicy="no-referrer"
                />
                <p className="text-sm text-gray-600">
                  Click or drag a new image to replace your avatar
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-32 h-32 rounded-full bg-gray-200 mx-auto flex items-center justify-center">
                  <svg
                    className="w-16 h-16 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <p className="text-sm text-gray-600">
                  Click or drag an image to upload your avatar
                </p>
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-2">
              JPG, PNG, or WebP • Max 5MB
            </p>
          </div>

          {/* Action buttons when no image selected */}
          <div className="flex gap-3 mt-4">
            {currentAvatarUrl && (
              <Button
                variant="outline"
                onClick={handleRemove}
                disabled={isUploading}
                loading={isUploading}
              >
                Remove Avatar
              </Button>
            )}
            {onCancel && (
              <Button variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div>
          {/* Cropper area */}
          <div className="relative w-full h-96 bg-gray-900 rounded-lg overflow-hidden">
            {/* @ts-expect-error react-easy-crop types incompatible with @types/react 18 */}
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </div>

          {/* Zoom slider */}
          <div className="mt-4">
            <label htmlFor="zoom" className="block text-sm font-medium text-gray-700 mb-2">
              Zoom
            </label>
            <input
              id="zoom"
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Action buttons when image selected */}
          <div className="flex gap-3 mt-4">
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={isUploading}
              loading={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Confirm'}
            </Button>
            <Button variant="outline" onClick={handleCancel} disabled={isUploading}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          {error}
        </div>
      )}
    </div>
  );
});

AvatarEditor.displayName = 'AvatarEditor';
